<script setup lang="ts">
import { X, FileText, Download, ExternalLink, Loader2 } from 'lucide-vue-next'
import { useWebChatStore } from '~/stores/web-chat'
import DocFilePreview from '~/components/documents/DocFilePreview.vue'
import { chatDocumentLink } from '~/utils/chatDocumentLinks'

// The split-panel document preview beside chat (MAR-437 follow-up). Opened when a workflow run
// produces exactly one document, replacing the auto-download / auto-navigate-away that used to fire
// on completion — the user asked to SEE the result without leaving the conversation or having a file
// silently land in their downloads folder.
//
// Read-only. `DocFilePreview` is the same renderer `/app/docs/[id]` uses for its own "File" and
// "Split" views, so a .docx/.pdf/image here looks identical to the full document page — there is no
// second preview implementation to keep in sync with the first.
const webChat = useWebChatStore()
const router = useRouter()

const documentId = computed(() => webChat.previewDocument?.documentId ?? null)
const name = computed(() => webChat.previewDocument?.name ?? '')
// `?v=` busts the viewer's own fetch cache when the SAME document id is regenerated in place
// (MAR-437 follow-up) — see the comment on `previewDocument` in the store. `DocxViewer` re-fetches
// only when this whole URL string changes, so the version must be part of it, not a sibling prop.
const fileUrl = computed(() => documentId.value
  ? `/api/items/${documentId.value}/file?v=${webChat.previewDocument?.version ?? 0}`
  : null)
const extension = computed(() => {
  const dot = name.value.lastIndexOf('.')
  return dot > 0 ? name.value.slice(dot + 1).toUpperCase() : ''
})

const downloading = ref(false)
async function download() {
  if (!documentId.value || downloading.value) return
  downloading.value = true
  try {
    await webChat.downloadDocument({ id: documentId.value, name: name.value })
  } finally {
    downloading.value = false
  }
}

function openFullPage() {
  if (!documentId.value) return
  const id = documentId.value
  webChat.closeDocPreview()
  router.push(chatDocumentLink(id, { activeCaseId: webChat.activeCaseId }))
}
</script>

<template>
  <div class="flex flex-col h-full bg-background">
    <!-- Header (the X closes the panel back to full-width chat — no separate "back" affordance). -->
    <div class="px-3 py-2 border-b shrink-0">
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <FileText class="h-4 w-4 shrink-0 text-primary" />
          <span class="text-sm font-medium truncate">{{ name }}</span>
          <span v-if="extension" class="text-[10px] font-medium text-muted-foreground shrink-0 rounded border border-border px-1 py-0.5">
            {{ extension }}
          </span>
        </div>
        <div class="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            class="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors disabled:opacity-50"
            :disabled="downloading"
            title="Descargar"
            @click="download"
          >
            <Loader2 v-if="downloading" class="h-4 w-4 animate-spin" />
            <Download v-else class="h-4 w-4" />
          </button>
          <button
            type="button"
            class="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors"
            title="Abrir en una página completa"
            @click="openFullPage"
          >
            <ExternalLink class="h-4 w-4" />
          </button>
          <button
            type="button"
            class="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent-lighter transition-colors"
            title="Cerrar"
            @click="webChat.closeDocPreview"
          >
            <X class="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>

    <DocFilePreview
      :src="fileUrl"
      :file-name="name"
      :has-file="!!fileUrl"
      :has-html="false"
    />
  </div>
</template>
